import { useState } from "react";
import { BadgeDollarSign, Printer } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MTopBar } from "@/components/mobile/MTopBar";
import { MPage, Loading, EmptyArt, Chip, cardCls, blueBtn } from "@/components/mobile/parts";
import { usePoll } from "@/hooks/useMobileData";
import { useAuth } from "@/context/AuthContext";
import { api, brl, fmtTime, ORIGIN_LABELS, PAY_LABELS } from "@/lib/api";
import { canSales, todaySales } from "@/lib/mobile";
import { printDoc, printErr } from "@/lib/btprint";
import { cn } from "@/lib/utils";

function SaleDialog({ sale, onClose }) {
  const { session } = useAuth();
  if (!sale) return null;
  const reprint = () => printDoc(session, "sale", `2ª via · Venda #${sale.number}`, { sale }).then(onClose).catch((e) => toast.error(printErr(e)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-sm overflow-y-auto" data-testid="m-sale-dialog">
        <DialogHeader><DialogTitle>Venda #{sale.number}</DialogTitle></DialogHeader>
        <div className="space-y-1 text-sm">
          {sale.items.map((i) => <div key={i.id} className="flex justify-between"><span>{i.qty}x {i.name}</span><span className="font-mono">{brl(i.total)}</span></div>)}
          <div className="flex justify-between border-t pt-2 font-bold"><span>Total</span><span className="font-mono">{brl(sale.total)}</span></div>
          {sale.payments.map((p, k) => <div key={k} className="flex justify-between text-slate-500"><span>{PAY_LABELS[p.method] || p.method}</span><span className="font-mono">{brl(p.amount)}</span></div>)}
          <p className="pt-2 text-xs text-slate-500">{ORIGIN_LABELS[sale.origin]} · {fmtTime(sale.created_at)} · {sale.user_name}</p>
        </div>
        {sale.status === "completed" && <Button className={cn("w-full", blueBtn)} onClick={reprint} data-testid="m-sale-reprint-btn"><Printer className="mr-2 h-4 w-4" />Reimprimir comprovante</Button>}
      </DialogContent>
    </Dialog>
  );
}

export default function MSales() {
  const { session } = useAuth();
  const allowed = canSales(session.user);
  const { data } = usePoll(() => (allowed ? api.get("/sales?limit=300").then((r) => todaySales(r.data)) : Promise.resolve(null)), 20000, [allowed]);
  const [open, setOpen] = useState(null);
  if (!allowed) return <><MTopBar title="Vendas" /><EmptyArt icon={BadgeDollarSign} title="Sem acesso às vendas" text="Peça ao administrador para liberar." /></>;
  return (
    <>
      <MTopBar title="Vendas de hoje" />
      <MPage>
        {!data ? <Loading /> : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-[#1565c0] p-4 text-white"><p className="text-xs text-white/75">Total vendido</p><p className="font-display text-2xl font-bold" data-testid="m-sales-total">{brl(data.total)}</p></div>
              <div className={cn(cardCls, "p-4")}><p className="text-xs text-slate-500">Vendas</p><p className="font-display text-2xl font-bold" data-testid="m-sales-count">{data.count}</p></div>
            </div>
            {!data.list.length ? <EmptyArt icon={BadgeDollarSign} title="Nenhuma venda hoje" testId="m-sales-empty" /> : (
              <ul className="space-y-2" data-testid="m-sales-list">
                {data.list.map((s) => (
                  <li key={s.id}>
                    <button onClick={() => setOpen(s)} className={cn(cardCls, "flex w-full items-center gap-3 p-3 text-left", s.status !== "completed" && "opacity-60")} data-testid={`m-sale-${s.number}`}>
                      <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#e3f2fd] font-mono text-sm font-bold text-[#0d47a1]">#{s.number}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold">{ORIGIN_LABELS[s.origin] || s.origin} · {fmtTime(s.created_at)}</span>
                        <span className="block truncate text-xs text-slate-500">{s.payments.map((p) => PAY_LABELS[p.method] || p.method).join(" + ")} · {s.user_name}</span>
                      </span>
                      {s.status !== "completed" ? <Chip tone="red">Cancelada</Chip> : <span className="font-mono font-bold">{brl(s.total)}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </MPage>
      <SaleDialog sale={open} onClose={() => setOpen(null)} />
    </>
  );
}
